import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, ShieldOff, FileWarning, RefreshCw, CheckCircle2, Pill, Mail, Send, X, ExternalLink, PhoneIncoming } from "lucide-react";

const KIND_META = {
  invalid_rx: { icon: FileWarning, color: "text-red-700 dark:text-red-400", bg: "bg-red-500/10 border-red-500/20", label: "Invalid Rx" },
  license_issue: { icon: ShieldOff, color: "text-amber-700 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", label: "License Issue" },
  missing_data: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/20", label: "Missing Data" },
  medication_request: { icon: Pill, color: "text-sky-700 dark:text-sky-400", bg: "bg-sky-500/10 border-sky-500/20", label: "Medication Request" },
  complaint: { icon: AlertTriangle, color: "text-rose-700 dark:text-rose-400", bg: "bg-rose-500/10 border-rose-500/20", label: "Complaint" },
  call_message: { icon: PhoneIncoming, color: "text-emerald-800 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", label: "Phone Message" },
};

const delivered = (m) => Math.round(((Number(m.fixed_price) || 0) + (Number(m.shipping_usd) || 0)) * 100) / 100;
const medLabel = (m) => `${m.display_name || m.name}${m.strength ? " " + m.strength : ""}`;

export default function AdminAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [replyFor, setReplyFor] = useState(null);
  const [medId, setMedId] = useState("");
  const [genericId, setGenericId] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([api.get("/admin/alerts"), api.get("/admin/pricing")]);
      setAlerts(a.data || []);
      setPricing((p.data || []).filter((m) => m.active !== false));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try { await api.post(`/admin/alerts/${id}/resolve`); toast.success("Resolved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openReply = (a) => { setReplyFor(a); setMedId(""); setGenericId(""); setNote(""); setSubject(""); };
  const closeReply = () => { if (!sending) setReplyFor(null); };

  const med = pricing.find((m) => m.pricing_id === medId);
  const generic = pricing.find((m) => m.pricing_id === genericId);

  useEffect(() => {
    if (med) setSubject(`Your PawLinx price for ${med.display_name || med.name}`);
  }, [medId]); // eslint-disable-line

  const sendReply = async () => {
    if (!medId) { toast.error("Select a medication from the Pricing Engine first"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/alerts/${replyFor.alert_id}/reply`, {
        medication_pricing_id: medId,
        generic_pricing_id: genericId || null,
        subject,
        note,
      });
      toast.success(`Email sent to ${data.to || "customer"}`);
      setReplyFor(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const filtered = alerts.filter((a) => filter === "all" || a.status === filter);

  const first = replyFor?.customer_first_name || "there";
  const requested = replyFor?.medication || "your medication";

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-5xl" data-testid="admin-alerts">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Inbox</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Alerts</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">Items flagged by the AI agents that need a human eye.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="alerts-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {["open", "resolved", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 h-8 rounded-md text-xs font-manrope transition-colors ${filter === f ? "bg-paw-accent text-white" : "bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/70"}`} data-testid={`alerts-filter-${f}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center font-manrope text-paw-text-muted dark:text-white/40">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-emerald-400/40" strokeWidth={1.5} />
          No {filter === "all" ? "" : filter} alerts. Everything's running smoothly.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => {
            const meta = KIND_META[a.kind] || { icon: AlertTriangle, color: "text-paw-text-muted dark:text-white/50", bg: "bg-black/[0.03] dark:bg-white/5 border-paw-border dark:border-white/10", label: a.kind };
            const Icon = meta.icon;
            const canReply = a.kind === "medication_request" && a.customer_email;
            return (
              <div key={a.alert_id} className={`border rounded-2xl p-5 ${meta.bg}`} data-testid={`alert-${a.alert_id}`}>
                <div className="flex items-start gap-4">
                  <Icon className={`w-5 h-5 ${meta.color} flex-shrink-0 mt-0.5`} strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className={`font-manrope text-xs font-semibold uppercase tracking-wider ${meta.color}`}>{meta.label}</p>
                      {a.status === "resolved" && <span className="font-manrope text-xs text-emerald-700 dark:text-emerald-400">· Resolved</span>}
                      {a.replied_at && <span className="font-manrope text-xs text-sky-700 dark:text-sky-300 flex items-center gap-1"><Mail className="w-3 h-3" /> Replied</span>}
                    </div>
                    <p className="font-manrope text-sm text-paw-text dark:text-white/90">{a.message}</p>
                    <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1">{a.ref_type ? `${a.ref_type}: ${a.ref_id?.slice(0, 8)}…` : ""} · {a.created_at?.slice(0, 19).replace("T", " ")}</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {canReply && (
                      <button onClick={() => openReply(a)} className="px-3 h-8 rounded-md bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-800 dark:text-sky-200 text-xs font-manrope transition-colors flex items-center gap-1.5" data-testid={`alert-reply-${a.alert_id}`}>
                        <Mail className="w-3 h-3" /> {a.replied_at ? "Reply again" : "Reply"}
                      </button>
                    )}
                    {a.status === "open" && (
                      <button onClick={() => resolve(a.alert_id)} className="px-3 h-8 rounded-md bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-xs font-manrope transition-colors flex items-center gap-1.5" data-testid={`alert-resolve-${a.alert_id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {replyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={closeReply}>
          <div className="w-full max-w-xl my-8 bg-white dark:bg-[#13161f] border border-paw-border dark:border-white/10 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-paw-border dark:border-white/10">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-sky-700 dark:text-sky-400" /><h2 className="font-outfit font-semibold text-paw-text dark:text-white">Reply with a price</h2></div>
              <button onClick={closeReply} className="p-1 rounded-md hover:bg-black/[0.06] dark:hover:bg-white/10 text-paw-text-muted dark:text-white/50"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div><span className="text-paw-text-muted dark:text-white/40 font-manrope">To </span><span className="text-paw-text dark:text-white/80 font-manrope">{replyFor.customer_email}</span></div>
                <div className="text-paw-text-muted dark:text-white/40 font-manrope text-xs">requested: {requested}</div>
              </div>

              {pricing.length === 0 ? (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-amber-800 dark:text-amber-200 font-manrope text-sm">
                  No medications in the Pricing Engine yet. Add this one first (with markup and delivery), then reply.
                  <button onClick={() => navigate("/admin/pricing")} className="mt-2 flex items-center gap-1.5 text-amber-700 dark:text-amber-100 underline text-xs">Open Pricing Engine <ExternalLink className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Medication (from Pricing Engine)</label>
                    <select value={medId} onChange={(e) => setMedId(e.target.value)} className="mt-1 w-full px-3 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm text-paw-text dark:text-white focus:outline-none focus:border-sky-500/50">
                      <option value="">Select a medication…</option>
                      {pricing.map((m) => (<option key={m.pricing_id} value={m.pricing_id}>{medLabel(m)} · ${delivered(m).toFixed(2)} delivered</option>))}
                    </select>
                    <button onClick={() => navigate("/admin/pricing")} className="mt-1.5 flex items-center gap-1 text-paw-text-muted dark:text-white/40 hover:text-paw-text-secondary dark:hover:text-white/70 text-xs font-manrope">Don't see it? Add it in the Pricing Engine <ExternalLink className="w-3 h-3" /></button>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Cheaper generic (optional)</label>
                    <select value={genericId} onChange={(e) => setGenericId(e.target.value)} className="mt-1 w-full px-3 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm text-paw-text dark:text-white focus:outline-none focus:border-sky-500/50">
                      <option value="">No generic available</option>
                      {pricing.filter((m) => m.pricing_id !== medId).map((m) => (<option key={m.pricing_id} value={m.pricing_id}>{medLabel(m)} · ${delivered(m).toFixed(2)} delivered</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Subject</label>
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 w-full px-3 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm text-paw-text dark:text-white focus:outline-none focus:border-sky-500/50" />
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Personal note (optional)</label>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an extra line if you want…" className="mt-1 w-full px-3 py-2 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm text-paw-text dark:text-white focus:outline-none focus:border-sky-500/50 resize-y" />
                  </div>

                  {med && (
                    <div className="rounded-lg bg-black/[0.02] dark:bg-white/[0.03] border border-paw-border dark:border-white/10 p-3">
                      <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-2">Preview</p>
                      <p className="font-manrope text-sm text-paw-text dark:text-white/80 leading-relaxed whitespace-pre-line">{
                        `Hi ${first},\n\nThanks for reaching out to PawLinx about ${requested} for your pet. Here ${generic ? "are your options" : "is your price"}:\n\n` +
                        `${medLabel(med)}: $${delivered(med).toFixed(2)} delivered to your door.\n   [ Choose this option ] → secure link\n` +
                        (generic
                          ? `\n${medLabel(generic)} (generic): $${delivered(generic).toFixed(2)} delivered to your door.\n   [ Choose the generic ] → secure link\n`
                          : "") +
                        (note ? `\n${note}\n` : "") +
                        `\nEach secure link lets you upload your prescription, add your shipping address, and pay by card. Links expire in 48 hours.\n\nWarm regards,\nThe PawLinx Team`
                      }</p>
                    </div>
                  )}
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Sends a branded PawLinx email from support@pawlinx.com (replies go to info@). Each option gets its own one-time secure checkout link (prescription upload + shipping address + card payment, valid 48h). Review it above, nothing goes out until you click Send.</p>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-paw-border dark:border-white/10">
              <button onClick={closeReply} disabled={sending} className="px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm text-paw-text-secondary dark:text-white/70 transition-colors disabled:opacity-60">Cancel</button>
              <button onClick={sendReply} disabled={sending || !medId} className="px-4 h-10 rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-manrope text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
